import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import queryString from 'query-string';
import cn from 'classnames';

import {
  evCancellationReasonSelection,
  evSubmitCancellationClick,
  pvCancellationConfirmationPageView
} from 'events/account';

import { fetchAccountInfo } from 'actions/account/account';
import { clearSelectedItems, confirmCancellation, fetchOrderDetails, updateCancellationReason } from 'actions/account/order';
import { firePixelServer } from 'actions/pixelServer';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import { PageLoader } from 'components/Loader';
import BackNav from 'components/account/BackNav';
import Breadcrumb from 'components/account/Breadcrumb';
import AccountHeader from 'containers/account/AccountHeader';
import CancelOrderTableLayout from 'components/account/Order/CancelOrderTableLayout';
import CancelOrderListLayout from 'components/account/Order/CancelOrderListLayout';
import MartyLink from 'components/common/MartyLink';
import { titaniteView, track } from 'apis/amethyst';
import styles from 'styles/containers/account/cancelOrder.scss';
import marketplace from 'cfg/marketplace.json';

const initialState = {
  selectedItems: []
};

const DEFAULT_CANCELLATION_MESSAGE = 'no-reason';

export class CancelOrder extends Component {
  state = initialState;

  componentDidMount() {
    titaniteView();

    const { firePixelServer, location: { search }, order: { order }, params: { orderId } } = this.props;
    const { lineItemId } = queryString.parse(search);
    const { updateCancellationReason } = this.props;

    // Redirect back to the order details page if required params are missing
    if (!lineItemId) {
      this.context.router.pushPreserveAppRoot(`/orders/${orderId}`);
    }

    // Check if cached order is different from requested order
    const staleData = order && (orderId !== order.orderId);

    // Fetch only if the order data is not loaded
    if (!order || staleData) {
      this.fetchData();
    }

    // find selected items from line items
    const selectedItems = this.getSelectedLineItems(order, lineItemId);

    if (!selectedItems.length) { // user refreshes cancel page
      this.context.router.pushPreserveAppRoot(`/orders/${orderId}`);
    } else {
      // set default cancellation reason
      selectedItems.forEach(lineItem => updateCancellationReason({ lineItem, reason: DEFAULT_CANCELLATION_MESSAGE }));

      this.setState({ selectedItems });
    }

    firePixelServer('order-cancel');
  }

  componentDidUpdate(prevProps) {
    const { order: { isCancellationConfirmed: prevIsCancellationConfirmed } } = prevProps;
    const {
      account: { cancelOrReturnItems },
      order: { order, isCancellationConfirmed }
    } = this.props;

    if (order && !prevIsCancellationConfirmed && isCancellationConfirmed) {
      const { orderId } = order;
      track(pvCancellationConfirmationPageView, { items: cancelOrReturnItems, amazonOrderId: orderId });
      this.fetchData();
    }
  }

  componentWillUnmount() {
    const { clearSelectedItems } = this.props;
    clearSelectedItems();
  }

  fetchData() {
    const { fetchOrderDetails, fetchAccountInfo, params } = this.props;
    const { orderId } = params;
    fetchOrderDetails(orderId);
    fetchAccountInfo();
  }

  getSelectedLineItems = (order, lineItemId) => {
    let selectedItems;

    if (Array.isArray(lineItemId)) {
      // why not just just `order.lineItems.filter(lineItem => lineItemId === lineItem.id)`?
      // lineItemId is not unique and if multiple of the same asin are in the order, filter will return all matches even if only cancelling one of those
      // we'll keep track of matched items and use line item object references for comparisons instead of id in the UPDATE_CANCELLATION_REASON reducer
      const lineItemIds = lineItemId.map(id => id);
      selectedItems = order.lineItems.reduce((acc, lineItem) => {
        const index = lineItemIds.indexOf(lineItem.id);

        if (index >= 0) {
          acc.push(lineItem);
          lineItemIds.splice(index, 1);
        }

        return acc;
      }, []);
    } else {
      if (order?.lineItems) {
        selectedItems = (lineItem => lineItem && [lineItem] || [])(order.lineItems.find(lineItem => lineItemId === lineItem.id));
      } else {
        selectedItems = [];
      }
    }

    return selectedItems;
  }

  handleCancellationReasonChange = (lineItem, event) => {
    const { order: { order: { orderId } }, updateCancellationReason } = this.props;
    const { value } = event.target;

    track(evCancellationReasonSelection, { orderId, lineItem, reason: value });
    updateCancellationReason({ lineItem, reason: value });
  };

  handleSubmitCancellation = () => {
    const { order, confirmCancellation } = this.props;
    const items = order.cancellationReasons.map(reason => ({ lineItemId: reason.lineItem.id, reason: reason.reason }));

    track(evSubmitCancellationClick);
    confirmCancellation(order.order.orderId, items);
  };

  render() {
    const { order: { order, cancellationReasons, isLoading, isCancellationLoading, isCancellationConfirmed, isCancellationError }, params } = this.props;

    if (!order || isLoading) {
      return <PageLoader centered={true}/>;
    }

    const { orderId, lineItems } = order;
    const staleData = params.orderId !== order.orderId;

    if (!isLoading && lineItems && !staleData) {
      const { selectedItems } = this.state;
      const cancellationLabel = isCancellationConfirmed ? 'Your Cancelled Item(s)' : 'Cancelling Items';
      const items = [
        { name: `Order #${orderId}`, url: `/orders/${orderId}` },
        { name: cancellationLabel }
      ];

      return (
        <SiteAwareMetadata>
          <div className={styles.cancelOrder}>
            <div className={styles.cancelOrderHeader}>
              <Breadcrumb items={items}/>
              <BackNav to={`/orders/${orderId}`}>Order #{orderId}</BackNav>
              <AccountHeader title={cancellationLabel}/>
            </div>

            {isCancellationConfirmed && (
              <div className={cn(styles.message, styles.confirmationMessage)}>
                Thanks! Your cancellation request has been successfully processed.
                Your status has been updated on your original order at <MartyLink to={`/orders/${orderId}`}>#{orderId}</MartyLink>.
              </div>
            )}

            {isCancellationError && (
              <div className={cn(styles.message, styles.errorMessage)}>
                Whoops! There was an issue submitting your cancellation request.
                Please try again or contact customer service at {marketplace.phoneNumber}.
                View the full details of your order at <MartyLink to={`/orders/${orderId}`}>#{orderId}</MartyLink>.
              </div>
            )}

            <div className={styles.desktop}>
              <CancelOrderTableLayout
                lineItems={selectedItems}
                cancellationReasons={cancellationReasons}
                isCancellationConfirmed={isCancellationConfirmed}
                onCancellationReasonChange={this.handleCancellationReasonChange}/>
            </div>

            <div className={styles.mobile}>
              <CancelOrderListLayout
                lineItems={selectedItems}
                cancellationReasons={cancellationReasons}
                isCancellationConfirmed={isCancellationConfirmed}
                onCancellationReasonChange={this.handleCancellationReasonChange}/>
            </div>

            {!isCancellationConfirmed && (
              <div className={styles.cancelOrderFooter}>
                <button type="button" onClick={this.handleSubmitCancellation} disabled={isCancellationLoading}>
                  {isCancellationLoading ? 'Submitting...' : 'Submit Cancellation' }
                </button>
              </div>
            )}
          </div>
        </SiteAwareMetadata>
      );
    }
  }
}

CancelOrder.contextTypes = {
  router: PropTypes.object.isRequired,
  testId: PropTypes.func
};

const mapStateToProps = state => ({
  order: state.order,
  account: state.account
});

export default connect(mapStateToProps, {
  fetchOrderDetails,
  fetchAccountInfo,
  firePixelServer,
  clearSelectedItems,
  updateCancellationReason,
  confirmCancellation
})(CancelOrder);
